<template>
  <div class="submit-claim-page tw-min-h-screen">
    <div class="submit-claim-shell">
      <!-- Bot-protection gate: the claim flow only renders after the backend
           verifies the Turnstile token server-side. The token is validated once
           here, before the form is shown; nothing is carried into the
           submission. -->
      <section v-show="screen === 'verify'" class="verify-gate">
        <div class="verify-gate-card">
          <img class="verify-gate-logo" src="@/assets/header.png" alt="APA" />
          <h1 class="verify-gate-title">APA Insurance</h1>
          <p class="verify-gate-copy">
            Confirm you're human to start your claim.
          </p>
          <div class="verify-gate-widget">
            <turnstile-widget
              ref="turnstile"
              action="claim-gate"
              @verified="onVerified"
              @error="onCaptchaError"
            />
          </div>
          <p v-if="verifying" class="verify-gate-status">Verifying…</p>
          <p v-else-if="gateError" class="verify-gate-error">
            {{ gateError }}
          </p>
        </div>
      </section>

      <landing-screen
        v-if="screen === 'landing'"
        @start="setScreen('choice')"
        @start-windscreen="setScreen('windscreen')"
      />
      <choice-screen
        v-else-if="screen === 'choice'"
        @choose-form="setScreen('form')"
        @choose-upload="setScreen('upload')"
        @choose-ai="setScreen('voice')"
        @back="setScreen('landing')"
      />
      <upload-path
        v-else-if="screen === 'upload'"
        @back="setScreen('choice')"
        @submit="submitUpload"
      />
      <!-- keyed: both branches are the same component, and without distinct
           keys Vue patches one instance instead of rebuilding it, so `mode`
           would change while the journey it started in stays put -->
      <motor-claim-form
        v-else-if="screen === 'form'"
        key="form"
        @back="setScreen('choice')"
      />
      <motor-claim-form
        v-else-if="screen === 'voice'"
        key="voice"
        mode="voice"
        @back="setScreen('choice')"
      />
      <windscreen-claim-form
        v-else-if="screen === 'windscreen'"
        @back="setScreen('landing')"
      />
      <success-screen
        v-else-if="screen === 'success'"
        :claim-ref="claimRef"
        @done="reset"
        @back="setScreen('choice')"
      />
      <failed-screen
        v-else-if="screen === 'failed'"
        @retry="retry"
        @back="setScreen('choice')"
      />
    </div>

    <v-overlay :value="submitting" z-index="60">
      <v-card light class="tw-rounded-xl tw-p-8 tw-text-center" max-width="300">
        <v-progress-circular indeterminate color="primary" size="46" />
        <div class="tw-font-bold tw-mt-4">Submitting your claim…</div>
        <div class="tw-text-xs c-gray-text tw-mt-2">
          Staging your details securely for review.
        </div>
      </v-card>
    </v-overlay>
  </div>
</template>

<script>
import LandingScreen from '@/components/claim-submission/LandingScreen'
import ChoiceScreen from '@/components/claim-submission/ChoiceScreen'
import UploadPath from '@/components/claim-submission/UploadPath'
import MotorClaimForm from '@/components/motor-claim/MotorClaimForm'
import WindscreenClaimForm from '@/components/motor-claim/windscreen/WindscreenClaimForm'
import SuccessScreen from '@/components/claim-submission/SuccessScreen'
import FailedScreen from '@/components/claim-submission/FailedScreen'
import TurnstileWidget from '@/components/claim-submission/TurnstileWidget'
import {
  collectDeviceDetails,
  encodeDeviceRef,
  getGeolocation,
} from '@/utils/deviceRef'
import {
  submitClaimForm,
  submitUploadedClaim,
  verifyGate,
  uploadClaimAttachment,
} from '@/services/claim-service'
import { DRAFT_KEYS, clearDraft } from '@/utils/claimDraft'
import { track } from '@/utils/analytics'

export default {
  name: 'SubmitClaim',
  components: {
    LandingScreen,
    ChoiceScreen,
    UploadPath,
    MotorClaimForm,
    WindscreenClaimForm,
    SuccessScreen,
    FailedScreen,
    TurnstileWidget,
  },
  data() {
    return {
      // start on the bot-protection gate; the flow renders only once the backend
      // verifies the Turnstile token
      screen: 'verify',
      lastScreen: 'form',
      submitting: false,
      deviceDetails: null,
      deviceRef: '',
      claimRef: '',
      // true while the gate token is being verified server-side
      verifying: false,
      // client-safe message shown on the gate when verification fails
      gateError: '',
    }
  },
  created() {
    this.deviceDetails = collectDeviceDetails()
    this.deviceRef = encodeDeviceRef(this.deviceDetails)
    if (this.$route.params.deviceRef !== this.deviceRef) {
      // reflect the reference in the URL so shares/refreshes carry it;
      // replace() keeps history clean
      this.$router
        .replace({
          name: 'SubmitClaim',
          params: { deviceRef: this.deviceRef },
        })
        .catch(() => {})
    }
  },
  methods: {
    setScreen(screen) {
      this.screen = screen
      track('claim_step', { step: screen })
      this.$nextTick(() => window.scrollTo({ top: 0 }))
    },
    async onVerified(token) {
      // the widget solved client-side; confirm it with the backend before
      // rendering the form. The token is single-use and never leaves this step.
      this.verifying = true
      this.gateError = ''
      try {
        const { data } = await verifyGate(token)
        if (!data || !data.success) throw new Error(data && data.msg)
        this.setScreen('landing')
      } catch (e) {
        // fail closed: keep the gate locked and let the user try again
        this.gateError =
          (e && e.message) ||
          "We couldn't verify you're human. Please try again."
        if (this.$refs.turnstile) this.$refs.turnstile.reset()
      } finally {
        this.verifying = false
      }
    },
    onCaptchaError() {
      // fail closed: without a solved widget the gate stays locked; the widget
      // surfaces its own retry message
      this.gateError = ''
    },
    async buildContext() {
      // geolocation is requested only at submit time (explicit user action);
      // a denied prompt resolves to null and never blocks submission
      const location = await getGeolocation()
      return {
        deviceDetails: this.deviceDetails,
        deviceRef: this.deviceRef,
        location,
      }
    },
    async uploadOne(file, regNo, type) {
      const { data } = await uploadClaimAttachment(file, regNo, type)
      if (!data || !data.success) throw new Error(data && data.msg)
      return data.data
    },
    async uploadAttachments(photos, docs, sketch, regNo) {
      const attachments = []
      for (const [key, fileObj] of Object.entries(photos)) {
        if (fileObj)
          attachments.push(
            await this.uploadOne(fileObj.file, regNo, `photo_${key}`)
          )
      }
      for (const [key, fileObj] of Object.entries(docs)) {
        if (fileObj)
          attachments.push(
            await this.uploadOne(fileObj.file, regNo, `doc_${key}`)
          )
      }
      if (sketch)
        attachments.push(await this.uploadOne(sketch.file, regNo, 'sketch'))
      return attachments
    },
    async submitForm(payload) {
      this.lastScreen = 'form'
      await this.runSubmit(async (context) => {
        const {
          photos = {},
          docs = {},
          sketch = null,
        } = payload.attachments || {}
        const attachments = await this.uploadAttachments(
          photos,
          docs,
          sketch,
          payload.form.vehicleRegistrationNumber
        )
        return submitClaimForm({ ...payload, ...context, attachments })
      })
    },
    // UploadPath.vue owns its own review/done screens (like MotorClaimForm),
    // so it reports the outcome back through a callback instead of this
    // driving `screen`/`submitting` the way the (unused) form path's
    // runSubmit does. payload.attachments already holds each document's
    // {type, url} — UploadPath uploads them as they're captured — so there's
    // nothing left to upload here.
    // The claim reference shown on UploadPath's done screen is the one
    // captured at initiate-claim, not anything from this response.
    async submitUpload(payload, callback) {
      try {
        const context = await this.buildContext()
        const { data } = await submitUploadedClaim({ ...payload, ...context })
        if (!data || !data.success) {
          callback({ success: false, msg: data && data.msg })
          return
        }
        clearDraft(DRAFT_KEYS.upload)
        callback({ success: true })
      } catch {
        callback({
          success: false,
          msg: 'Could not submit your claim. Please try again.',
        })
      }
    },
    async runSubmit(request) {
      this.submitting = true
      try {
        const context = await this.buildContext()
        const { data } = await request(context)
        if (!data || !data.success) throw new Error(data && data.msg)
        const staged = data.data || {}
        this.claimRef = String(staged.id || staged.regNo || '')
        // the claim is in; drop the saved draft for whichever path submitted
        clearDraft(DRAFT_KEYS[this.lastScreen])
        this.setScreen('success')
      } catch {
        // any submission failure routes to the retry screen; entered data is
        // preserved so nothing is lost
        this.setScreen('failed')
      } finally {
        this.submitting = false
        window.scrollTo({ top: 0 })
      }
    },
    retry() {
      this.setScreen(this.lastScreen)
    },
    reset() {
      this.setScreen('landing')
      this.claimRef = ''
    },
  },
}
</script>

<style scoped>
.submit-claim-page {
  background: radial-gradient(
      circle at top left,
      rgba(248, 171, 16, 0.12),
      transparent 32rem
    ),
    linear-gradient(135deg, #f4f7fb 0%, #eef3f8 45%, #e8eef6 100%);
  padding: 24px;
  /* dvh keeps the wrapper within the visible viewport under mobile browser
     toolbars; blocking overscroll stops rubber-banding from dragging the
     app shell */
  min-height: 100vh;
  min-height: 100dvh;
  overscroll-behavior-y: none;
}

.submit-claim-shell {
  /* 50% + 590px halves the leftover side space of the old 1180px cap on any
     viewport wider than 1180px; identical below it */
  width: min(100%, calc(50% + 590px));
  min-height: calc(100vh - 48px);
  min-height: calc(100dvh - 48px);
  margin: 0 auto;
  overflow: hidden;
  background: #fff;
  border-radius: 24px;
  /* the hairline is an inset ring, not a border: a real 1px border adds 2px
     to the shell's height beyond the screens' calc(100vh - 48px), making the
     page scroll by exactly 2px even when everything fits */
  box-shadow: inset 0 0 0 1px rgba(17, 76, 172, 0.08),
    0 24px 70px rgba(17, 76, 172, 0.12);
}

.verify-gate {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: calc(100vh - 48px);
  min-height: calc(100dvh - 48px);
  padding: 32px;
}

.verify-gate-card {
  width: 100%;
  max-width: 420px;
  text-align: center;
}

.verify-gate-logo {
  display: block;
  width: 140px;
  margin: -24px auto 24px;
}

.verify-gate-title {
  margin: 0;
  color: #14213d;
  font-size: 26px;
  font-weight: 800;
}

.verify-gate-copy {
  margin: 12px 0 24px;
  color: #4d4d4e;
  font-size: 15px;
  line-height: 1.6;
}

.verify-gate-widget {
  display: flex;
  justify-content: center;
}

.verify-gate-status {
  margin: 16px 0 0;
  color: #4d4d4e;
  font-size: 14px;
  font-weight: 600;
}

.verify-gate-error {
  margin: 16px 0 0;
  color: #c0392b;
  font-size: 14px;
  font-weight: 700;
}

@media (max-width: 640px) {
  .submit-claim-page {
    padding: 0;
    background: #fff;
  }

  .submit-claim-shell {
    min-height: 100vh;
    min-height: 100dvh;
    border-radius: 0;
    box-shadow: none;
  }
}
</style>
